import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import ComicCard from '@/components/base/ComicCard';
import { getComicBySlug, getRelatedComics } from '@/mocks/comics';
import type { BreadcrumbItem } from '@/components/feature/Breadcrumb';

export default function ComicPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = getComicBySlug(slug || '');
  const [activeTab, setActiveTab] = useState<'chapters' | 'info'>('chapters');
  const [currentPage, setCurrentPage] = useState(1);
  const chaptersPerPage = 10;

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-24 h-24 mx-auto flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-4xl text-foreground-300" />
            </div>
            <h1 className="mt-6 font-heading text-2xl text-foreground-800">漫画未找到</h1>
            <p className="mt-2 text-foreground-500">该漫画不存在或已下架</p>
            <Link
              to="/category"
              className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              返回漫画列表
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    { name: '首页', path: '/' },
    { name: '漫画分类', path: '/category' },
    { name: comic.category, path: `/category/${comic.categorySlug}` },
    { name: comic.title },
  ];

  const totalPages = Math.ceil(comic.chapters.length / chaptersPerPage);
  const paginatedChapters = comic.chapters.slice(
    (currentPage - 1) * chaptersPerPage,
    currentPage * chaptersPerPage
  );
  const relatedComics = getRelatedComics(comic, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title}漫画 - 布丁漫画网在线免费阅读`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.chapters[0]?.updateDate,
    dateModified: comic.updateDate,
    image: comic.coverImage,
    url: `https://budingmanhua.com/comic/${comic.slug}`,
    publisher: { '@type': 'Organization', name: '布丁漫画网', url: 'https://budingmanhua.com' },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}漫画在哪里可以免费阅读？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `布丁漫画网提供${comic.title}漫画全集下拉式免费阅读，无删减内容，流畅加载，全章节覆盖。访问布丁漫画网即可开始阅读。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}漫画是否已经完结？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}漫画目前${comic.status}，共 ${comic.chapters.length} 话。布丁漫画网会第一时间同步更新最新章节。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}漫画有多少章节？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}漫画共有 ${comic.chapters.length} 话，每一话都可以在布丁漫画网流畅阅读，支持下拉式连续浏览。`,
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${comic.title}漫画全集下拉式免费阅读 - 布丁漫画网`}
        description={`${comic.title}漫画全集下拉式免费阅读就在布丁漫画网。${comic.description} 作者：${comic.author}，${comic.status}，共${comic.chapters.length}话，评分${comic.rating}分。`}
        keywords={`${comic.title},漫画,全集,下拉式阅读,免费漫画,${comic.category},布丁漫画网,${comic.author}`}
        canonicalUrl={`https://budingmanhua.com/comic/${comic.slug}`}
        ogType="article"
        ogImage={comic.coverImage}
        schemaJsonLd={[articleSchema, faqSchema]}
      />
      <Navbar />
      <main>
        <div className="pt-24 pb-14 md:pb-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <div className="md:col-span-1">
                <div className="sticky top-24">
                  <div className="relative overflow-hidden rounded-xl aspect-[3/4] bg-background-200">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title}漫画封面`}
                      title={`${comic.title} - 布丁漫画网`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <button
                      type="button"
                      className="flex-1 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-book-open-line mr-1" />
                      开始阅读
                    </button>
                    <button
                      type="button"
                      className="flex items-center justify-center w-11 h-11 rounded-lg border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer"
                      aria-label="收藏"
                    >
                      <i className="ri-heart-line text-lg" />
                    </button>
                    <button
                      type="button"
                      className="flex items-center justify-center w-11 h-11 rounded-lg border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer"
                      aria-label="分享"
                    >
                      <i className="ri-share-line text-lg" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <span className="text-sm text-foreground-500">作者：<strong className="text-foreground-700">{comic.author}</strong></span>
                  <span className="text-foreground-300">|</span>
                  <span className={`text-sm font-medium ${comic.status === '连载中' ? 'text-accent-500' : 'text-foreground-500'}`}>
                    {comic.status}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-500">共 <strong className="text-foreground-700">{comic.chapters.length}</strong> 话</span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-500">
                    更新至 {comic.chapters[comic.chapters.length - 1]?.title}
                  </span>
                </div>

                <div className="flex items-center gap-4 mt-3">
                  <span className="flex items-center gap-1 text-accent-500">
                    <i className="ri-star-fill" />
                    <strong className="text-lg">{comic.rating}</strong>
                    <span className="text-xs text-foreground-400">分</span>
                  </span>
                  <span className="text-sm text-foreground-400">
                    <i className="ri-eye-line mr-1" />
                    {(comic.views / 10000).toFixed(1)}万阅读
                  </span>
                  <span className="text-sm text-foreground-400">
                    <i className="ri-heart-line mr-1" />
                    {(comic.favorites / 10000).toFixed(1)}万收藏
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {comic.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${comic.categorySlug}`}
                      className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full hover:bg-secondary-200 transition-colors cursor-pointer"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>

                <p className="mt-5 text-sm text-foreground-600 leading-relaxed">
                  {comic.description}
                </p>

                <div className="mt-8">
                  <div className="flex items-center border-b border-background-200">
                    <button
                      type="button"
                      onClick={() => setActiveTab('chapters')}
                      className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                        activeTab === 'chapters'
                          ? 'border-primary-500 text-primary-500'
                          : 'border-transparent text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      章节列表
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('info')}
                      className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                        activeTab === 'info'
                          ? 'border-primary-500 text-primary-500'
                          : 'border-transparent text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      作品信息
                    </button>
                  </div>

                  {activeTab === 'chapters' && (
                    <div className="mt-4">
                      <div className="space-y-1">
                        {paginatedChapters.map((ch) => (
                          <div
                            key={ch.id}
                            className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors">
                                {ch.title}
                              </span>
                              {ch.chapterNumber <= 3 && (
                                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-primary-100 text-primary-600 rounded">
                                  NEW
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-foreground-400">
                              <span>{ch.pageCount}P</span>
                              <span>{ch.updateDate}</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-6">
                          <button
                            type="button"
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-foreground-500 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                          >
                            <i className="ri-arrow-left-s-line" />
                          </button>
                          {Array.from({ length: totalPages }, (_, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setCurrentPage(i + 1)}
                              className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-colors cursor-pointer ${
                                currentPage === i + 1
                                  ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                                  : 'text-foreground-500 hover:bg-background-100'
                              }`}
                            >
                              {i + 1}
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-foreground-500 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                          >
                            <i className="ri-arrow-right-s-line" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'info' && (
                    <dl className="mt-4 space-y-3">
                      <div className="flex py-2 border-b border-background-100">
                        <dt className="w-20 text-sm text-foreground-400 flex-shrink-0">作品名称</dt>
                        <dd className="text-sm text-foreground-700">{comic.title}</dd>
                      </div>
                      <div className="flex py-2 border-b border-background-100">
                        <dt className="w-20 text-sm text-foreground-400 flex-shrink-0">作者</dt>
                        <dd className="text-sm text-foreground-700">{comic.author}</dd>
                      </div>
                      <div className="flex py-2 border-b border-background-100">
                        <dt className="w-20 text-sm text-foreground-400 flex-shrink-0">分类</dt>
                        <dd className="text-sm text-foreground-700">{comic.category}</dd>
                      </div>
                      <div className="flex py-2 border-b border-background-100">
                        <dt className="w-20 text-sm text-foreground-400 flex-shrink-0">状态</dt>
                        <dd className="text-sm text-foreground-700">{comic.status}</dd>
                      </div>
                      <div className="flex py-2 border-b border-background-100">
                        <dt className="w-20 text-sm text-foreground-400 flex-shrink-0">评分</dt>
                        <dd className="text-sm text-foreground-700">{comic.rating} 分</dd>
                      </div>
                      <div className="flex py-2 border-b border-background-100">
                        <dt className="w-20 text-sm text-foreground-400 flex-shrink-0">章节</dt>
                        <dd className="text-sm text-foreground-700">共 {comic.chapters.length} 话</dd>
                      </div>
                      <div className="flex py-2 border-b border-background-100">
                        <dt className="w-20 text-sm text-foreground-400 flex-shrink-0">更新</dt>
                        <dd className="text-sm text-foreground-700">{comic.updateDate}</dd>
                      </div>
                    </dl>
                  )}
                </div>
              </div>
            </div>

            {relatedComics.length > 0 && (
              <div className="mt-14 md:mt-20">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
                    相关推荐
                  </h2>
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    更多同类作品
                    <i className="ri-arrow-right-line ml-1 text-xs" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}